import React, { useState } from 'react';
import { B } from '../constants';
import { ai, destinations as destinationsAPI } from '../api';
import Modal from './ui/Modal';
import Input from './ui/Input';
import useDebouncedDestSearch from '../hooks/useDebouncedDestSearch';

const POPULAR = [
  { name: 'Italy, Rome' }, { name: 'Japan, Tokyo' }, { name: 'Greece, Athens' },
  { name: 'France, Paris' }, { name: 'Thailand, Bangkok' },
];

export default function AIItineraryModal({ onClose, onImport }) {
  const [destination, setDestination] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [days, setDays] = useState(5);
  const [budget, setBudget] = useState('');
  const [vibe, setVibe] = useState('mixed');
  const [interests, setInterests] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Shared debounced + gated + cached destination search (same as AIScreen).
  const { results: destinationSuggestions } = useDebouncedDestSearch(destination, {
    fetcher: destinationsAPI.autocomplete,
    select: (data) => (data && data.predictions) || [],
    fallback: (q) => POPULAR
      .filter(d => d.name.toLowerCase().includes(q.toLowerCase()))
      .map(d => ({
        description: d.name,
        place_id: '',
        mainText: d.name.split(',')[0].trim(),
        secondaryText: d.name.split(',').slice(1).join(',').trim(),
        countryCode: null,
      })),
  });

  const selectDestination = (dest) => {
    setDestination(dest.description || dest.mainText);
    setPlaceId(dest.place_id || dest.placeId || '');
    setShowSuggestions(false);
  };

  const handleGenerate = async () => {
    if (!destination) {
      setError('Destination is required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { itinerary } = await ai.generateItinerary({
        destination,
        placeId: placeId || undefined,
        days,
        budget: budget || null,
        vibe,
        interests: interests || null
      });

      const tripDays = itinerary.days.map(d => ({
        dayNumber: d.day,
        title: d.title,
        activities: d.activities.map((a, idx) => ({
          name: a.activity,
          time: a.time,
          location: a.location || a.description || '',
          // Preserve coords, photo, and the approximate-location flag in the notes
          // JSON (same shape as the main planner's save) so the saved trip keeps its
          // map pins and shows the approximate-location badge in TripDetailScreen.
          notes: a.lat != null && a.lng != null
            ? JSON.stringify({ _lat: a.lat, _lng: a.lng, photoUrl: a.photoUrl || '', _approx: a.locationApproximate || undefined, description: (a.description || a.tip || '').slice(0, 1500) })
            : (a.tip || a.notes || '')
        }))
      }));

      setDestination(itinerary.title.split(' in ')[1] || destination);

      onImport(tripDays, itinerary.title.split(' in ')[0] || `Trip to ${destination}`);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to generate itinerary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="✨ AI Itinerary Generator" size="md" ariaLabel="AI Itinerary Generator">
      <p style={{ color: B.muted, marginBottom: 20, fontSize: 14 }}>
        Describe your dream trip and AI will generate a detailed itinerary for you.
      </p>

      <div style={{ marginBottom: 16, position: 'relative' }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Destination *</label>
        <Input
          type="text"
          placeholder="e.g., Italy, Japan, Greece"
          value={destination}
          onChange={e => { setDestination(e.target.value); setPlaceId(''); setShowSuggestions(true); }}
        />

        {/* Place autocomplete dropdown — grounds the name in a Google placeId. */}
        {showSuggestions && destinationSuggestions.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 12, border: '1px solid #e5e0d8', marginTop: 6, zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto', padding: 6 }}>
            {destinationSuggestions.map((d, i) => (
              <div
                key={d.place_id || i}
                onClick={() => selectDestination(d)}
                style={{ padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: "'Montserrat',sans-serif", color: B.night }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontWeight: 700 }}>{d.mainText || d.description}</div>
                {(d.secondaryText || d.description) && d.mainText && (
                  <div style={{ fontSize: 12, color: '#888' }}>{d.secondaryText || d.description}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Number of Days</label>
          <Input type="number" value={days} onChange={e => setDays(parseInt(e.target.value, 10) || 1)} min={1} max={30} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Budget (USD)</label>
          <Input type="number" value={budget} onChange={e => setBudget(e.target.value)} placeholder="Optional" />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Vibe</label>
        <select style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #e5e0d8', fontSize: 14, fontFamily: "'Montserrat',sans-serif", color: B.night, background: 'white' }} value={vibe} onChange={e => setVibe(e.target.value)}>
          <option value="mixed">Mixed</option>
          <option value="adventure">Adventure</option>
          <option value="relaxation">Relaxation</option>
          <option value="culture">Culture</option>
          <option value="food">Food & Wine</option>
          <option value="nature">Nature</option>
        </select>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Interests (comma separated)</label>
        <Input type="text" value={interests} onChange={e => setInterests(e.target.value)} placeholder="e.g., museums, hiking, beaches, nightlife" />
      </div>

      {error && (
        <div style={{ color: '#dc3545', marginBottom: 16, fontSize: 14 }}>{error}</div>
      )}

      <button
        onClick={handleGenerate}
        disabled={loading}
        style={{
          width: '100%', padding: 14, background: loading ? '#ccc' : B.forest,
          color: 'white', border: 'none', borderRadius: 12, fontSize: 16,
          fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: "'Montserrat',sans-serif"
        }}
      >
        {loading ? 'Generating...' : 'Generate Itinerary'}
      </button>
    </Modal>
  );
}
